import { TuiButton } from '@taiga-ui/core';

import { ChangeDetectionStrategy, Component, forwardRef, inject, Input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FlattenedSequence } from '@pixacare/dtos';
import { DialogService } from 'src/app/services/dialog.service';
import { SequenceContext } from 'src/app/shared/models/enums/sequence-context.enum';
import { MediaClickedOutput } from 'src/app/shared/models/media-clicked-output';
import {
  SequencePickerDialogComponent,
  SequencePickerOutput,
} from '../sequence-picker/sequence-picker-dialog/sequence-picker-dialog.component';
import { SequenceStacksPreviewComponent } from '../sequence-stacks-preview/sequence-stacks-preview.component';

@Component({
  selector: 'pxc-sequence-picker-input',
  standalone: true,
  imports: [SequenceStacksPreviewComponent, TuiButton, TranslocoDirective],
  templateUrl: './sequence-picker-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col gap-2 items-start',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SequencePickerInputComponent),
      multi: true,
    },
  ],
})
export class SequencePickerInputComponent implements ControlValueAccessor {
  private readonly dialogService = inject(DialogService);
  private readonly translocoService = inject(TranslocoService);

  @Input() dialogTitle = this.translocoService.translate('Sélectionnez des séquences');
  @Input() dialogSaveLabel = this.translocoService.translate('Enregistrer');
  @Input() buttonLabel = this.translocoService.translate('Ajouter');
  @Input() buttonAppearance = 'outline';
  @Input() editOnMediaClick = false;

  @Input() patientId: number;
  @Input() clientCode: string;
  @Input() departmentIds: number[] = [];
  @Input() telemonitoringIds: number[] = [];
  @Input() sequenceContext: SequenceContext = SequenceContext.TELEMONITORING;

  protected readonly sequences = signal<FlattenedSequence[]>([]);
  protected readonly isDisabled = signal(false);

  private onChange: (value: FlattenedSequence[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: FlattenedSequence[]): void {
    this.sequences.set(value || []);
  }

  registerOnChange(fn: (value: FlattenedSequence[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  onMediaClick(): void {
    if (this.editOnMediaClick) {
      this.openSequencePicker();
    }
  }

  onMoreClick(): void {
    if (!this.isDisabled()) {
      this.openSequencePicker();
    }
  }

  openSequencePicker(): void {
    if (this.isDisabled()) {
      return;
    }

    this.onTouched();

    this.dialogService
      .openComponentWithCloseConfirmation<SequencePickerDialogComponent, SequencePickerOutput>(
        SequencePickerDialogComponent,
        {
          data: {
            patientId: this.patientId,
            clientCode: this.clientCode,
            departmentIds: this.departmentIds,
            telemonitoringIds: this.telemonitoringIds,
            preselectedSequences: this.sequences(),
            saveLabel: this.dialogSaveLabel,
          },
          label: this.dialogTitle,
          size: 'l',
        },
      )
      .subscribe((result) => {
        if (result) {
          this.sequences.set(result.selectedSequences);
          this.onChange(this.sequences());
        }
      });
  }

  deleteSequence(output: MediaClickedOutput): void {
    this.sequences.update((sequences) => sequences.filter((seq) => seq.sequenceInstance.id !== output.sequenceId));
    this.onChange(this.sequences());
  }
}
